/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Payout-frequency vocabulary — ONE source for the data identifiers, the
// legacy spellings still on disk and the i18n key each renders through. Plain
// JS on purpose: the client, the partner-portal server allowlist
// (server/partner/validateUpdateSubmission.ts) and the admin drawer all read
// it, and a src/lib module the tsc server build includes must never carry
// TS-only syntax.
//
// The stored value is a Dutch data identifier (providers_database.json,
// pending updates, the public API). Jasper fixed the vocabulary on 2026-09-10
// to the "Per …" family; everything else is legacy that still renders and is
// folded into the canonical spelling wherever a value is read, written or
// compared (`canonicalPayout`). The literal "Onbekend" is a missing datapoint,
// never a frequency (`isUnknownPayout`).
//
// FOUR values since 2026-10-07 (plan-voorwaarden-vergelijking D4): per maand,
// kwartaal, halfjaar, jaar — the only answers a visitor can compare. "Naar
// keuze" stays READABLE (a stored one still renders) until the fill writes the
// best frequency on offer plus a note; "Periodiek" and "Per afspraak" say
// nothing a visitor can use and read as a MISSING datapoint, exactly like
// "Onbekend" — the record itself is untouched, the research pass re-asks.

/** The writable data identifiers, in dropdown order (drawer, portal, server allowlist, fill). */
export const PAYOUT_FREQUENCIES = Object.freeze([
  'Per maand',
  'Per kwartaal',
  'Per halfjaar',
  'Per jaar',
]);

/**
 * Stored values that are missing datapoints, not frequencies (lower-cased):
 * the "Onbekend" placeholder and, since 2026-10-07, the two vague answers.
 */
const UNKNOWN_PAYOUT_VALUES = Object.freeze(['onbekend', 'periodiek', 'per afspraak']);

/**
 * Legacy spellings → canonical. 'Maandelijks' / 'Halfjaarlijks' / 'Dagelijks'
 * = the admin drawer before 2026-09-10 and CSV imports; 'Jaarlijks' /
 * 'Kwartaal' / 'Kwartaal of Jaarlijks' = the partner portal before 2026-09-10.
 * @type {Readonly<Record<string, string>>}
 */
export const LEGACY_PAYOUT_ALIASES = Object.freeze({
  Maandelijks: 'Per maand',
  Halfjaarlijks: 'Per halfjaar',
  Dagelijks: 'Per dag',
  Jaarlijks: 'Per jaar',
  Kwartaal: 'Per kwartaal',
  'Kwartaal of Jaarlijks': 'Naar keuze',
});

/**
 * i18n key suffix under `table.payout_values` per canonical identifier. 'Naar
 * keuze', 'Per dag' and 'Na de verkoop' are not offered in any dropdown but
 * still render should a record carry them. The keys for the renamed labels are
 * NEW (per_month, per_half_year, per_day, by_arrangement): the CMS saves the
 * full merged tree into i18n_admin, so an in-place change of the old `monthly`
 * value would stay masked by the frozen "Maandelijks" on acc and prd.
 *
 * 'Periodiek' and 'Per afspraak' left this map on 2026-10-07 (they read as
 * unknown); their `periodic` / `by_arrangement` keys stay in both baselines —
 * removing a shipped key buys nothing and an old payload may still name it.
 * @type {Readonly<Record<string, string>>}
 */
const CANONICAL_KEYS = Object.freeze({
  'Per maand': 'per_month',
  'Per kwartaal': 'quarterly',
  'Per halfjaar': 'per_half_year',
  'Per jaar': 'yearly',
  'Naar keuze': 'choice',
  'Per dag': 'per_day',
  'Na de verkoop': 'after_sale',
});

/**
 * Every spelling (canonical + legacy) → i18n key suffix. Kept for consumers
 * that index the map directly; prefer `payoutValueKey`.
 * @type {Readonly<Record<string, string>>}
 */
export const PAYOUT_VALUE_KEYS = Object.freeze({
  ...CANONICAL_KEYS,
  ...Object.fromEntries(
    Object.entries(LEGACY_PAYOUT_ALIASES).map(([legacy, canonical]) => [
      legacy,
      CANONICAL_KEYS[canonical],
    ])
  ),
});

/**
 * "Onbekend" is a missing datapoint, never a fact: older records store the
 * literal string as a value and the public flatten passes it through. Every
 * consumer must treat it exactly like an absent value — a dash on the card,
 * no fact row on the provider page, no completeness point in a score. Since
 * 2026-10-07 the same holds for "Periodiek" and "Per afspraak" (D4).
 * @param {string | null | undefined} v
 * @returns {boolean}
 */
export function isUnknownPayout(v) {
  const s = v ? String(v).trim().toLowerCase() : '';
  return !s || UNKNOWN_PAYOUT_VALUES.includes(s);
}

/**
 * The canonical spelling of a stored value, or null when unknown. A value
 * outside both vocabularies comes back trimmed but unchanged — visible, never
 * blanked, so a typo in the data shows up instead of hiding.
 * @param {string | null | undefined} v
 * @returns {string | null}
 */
export function canonicalPayout(v) {
  if (isUnknownPayout(v)) return null;
  const trimmed = String(v).trim();
  return LEGACY_PAYOUT_ALIASES[trimmed] ?? trimmed;
}

/**
 * i18n key suffix under `table.payout_values`, or null when unknown or unmapped.
 * @param {string | null | undefined} v
 * @returns {string | null}
 */
export function payoutValueKey(v) {
  const canonical = canonicalPayout(v);
  return canonical ? (CANONICAL_KEYS[canonical] ?? null) : null;
}

/**
 * The payout frequency as every PUBLIC payload carries it: the canonical
 * spelling, or null for a missing datapoint ("Onbekend", "Periodiek", "Per
 * afspraak", empty, not a string). ONE rule for the four builders
 * (`/api/providers`, the profile route and both builders in
 * scripts/prerender.mjs), so a raw "Onbekend" can no longer count as known in
 * one mirror and as unknown in the other.
 *
 * @param {unknown} raw  provider.payoutFrequency as stored
 * @returns {string | null}
 */
export function publicPayoutFrequency(raw) {
  return typeof raw === 'string' ? canonicalPayout(raw) : null;
}
